import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mail, MapPin } from "lucide-react";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { useContent } from "../context/ContentContext";
import { t } from "../lib/i18n";
import { CText } from "../components/CText";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";

export default function Contact() {
  const { lang } = useLanguage();
  const { get } = useContent();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      trackEvent("submit_contact_form", {
        has_phone: !!form.phone,
        message_length: form.message.length,
      });
      toast.success(t(lang, "form_success"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error(t(lang, "form_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-40 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <div className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-4">{t(lang, "nav_contact")}</div>
            <CText section="contact" field="title" as="h1" className="font-serif-display text-5xl md:text-7xl text-white leading-[0.95] mb-6" />
            <CText section="contact" field="sub" as="p" className="text-[#A3A9B2] max-w-xl text-base md:text-lg leading-relaxed" />
          </motion.div>

          <div className="grid lg:grid-cols-[1fr_1fr] gap-16 lg:gap-24 mt-20">
            {/* Form */}
            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }}
              className="space-y-8"
              data-testid="contact-form"
            >
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase text-[#C5A059] mb-2 block">{t(lang, "form_name")}</label>
                <input type="text" value={form.name} onChange={onChange("name")} required
                  className="editorial-input w-full py-3 text-lg" data-testid="contact-name" />
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="text-[10px] tracking-[0.3em] uppercase text-[#C5A059] mb-2 block">{t(lang, "form_email")}</label>
                  <input type="email" value={form.email} onChange={onChange("email")} required
                    className="editorial-input w-full py-3 text-lg" data-testid="contact-email" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.3em] uppercase text-[#C5A059] mb-2 block">{t(lang, "form_phone")}</label>
                  <input type="tel" value={form.phone} onChange={onChange("phone")}
                    className="editorial-input w-full py-3 text-lg" data-testid="contact-phone" />
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase text-[#C5A059] mb-2 block">{t(lang, "form_message")}</label>
                <textarea value={form.message} onChange={onChange("message")} required rows={6}
                  className="editorial-input w-full py-3 text-lg resize-none" data-testid="contact-message" />
              </div>
              <button type="submit" disabled={submitting}
                className="inline-flex items-center gap-3 bg-[#C5A059] text-[#0A0F16] px-10 py-4 text-sm tracking-[0.2em] uppercase font-medium hover:bg-[#E5C07B] transition-colors disabled:opacity-50"
                data-testid="contact-submit">
                {submitting ? "..." : t(lang, "form_submit")} <Send size={14} />
              </button>
            </motion.form>

            {/* Info */}
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.4 }}
              className="lg:pl-12 lg:border-l lg:border-[#C5A059]/15 space-y-10">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A9B2] mb-3 flex items-center gap-2"><Mail size={12}/>Email</div>
                <a href={`mailto:${get("contact", "email", "impeclandpy@gmail.com")}`} className="font-serif-display text-2xl md:text-3xl text-[#C5A059] link-copper break-all">
                  {get("contact", "email", "impeclandpy@gmail.com")}
                </a>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A9B2] mb-3 flex items-center gap-2"><MapPin size={12}/>{t(lang,"location")}</div>
                <CText section="contact" field="location_main" as="div" className="font-serif-display text-2xl md:text-3xl text-white" />
                <CText section="contact" field="location_sub" as="p" className="text-[#A3A9B2] text-sm mt-2" />
              </div>
              <div className="pt-10 border-t border-[#C5A059]/15">
                <CText section="contact" field="quote" as="blockquote" className="font-serif-display text-xl md:text-2xl italic text-white/80 leading-relaxed" />
                <div className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mt-4">— <CText section="contact" field="quote_author" as="span" /></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
